import { requests } from '@fable/api'
import { Loader, marginEdgesRules } from '@fable/components'
import { css, useTheme } from '@fable/theme'
import { analytics } from 'analytics'
import { HomeClubsDiscovery } from 'app/marketing/components/home_clubs_discovery'
import { useHome } from 'app/marketing/context'
import { ClubCard } from 'components/cards'
import { SearchFilterResults } from 'components/search_filters/SearchFilterResults'
import { useEffect, useRef } from 'react'
import { useQuery } from 'react-query'
import { ClubListResponse } from 'types'
import SuspenseWithErrorBoundary from '../../../../components/suspense_with_error_boundary/SuspenseWithErrorBoundary'
import { lazyWithRetry } from '../../../../utils'
import { DownloadAppCard } from '../../components/download_app_card'
import { HomeHero } from './hero/HomeHero'

const HomeEvent = lazyWithRetry(() => import('../../components/HomeEvent'))
const HomeClubsList = lazyWithRetry(
  () => import('../../components/home_clubs_list/HomeClubsList')
)

const Home = () => {
  const { colors, mediaQueries } = useTheme()
  const { activeFilter } = useHome()
  const isFeatured = activeFilter === 'featured'
  const clubsRef = useRef<HTMLDivElement>(null)

  const { data, isFetching, refetch } = useQuery(
    ['clubs_by_genre'],
    () => requests.getClubsByGenre(activeFilter),
    { enabled: !isFeatured }
  )

  const filteredClubs = data?.data as ClubListResponse

  useEffect(() => {
    if (!isFeatured) refetch()
  }, [isFeatured, refetch, activeFilter])

  useEffect(() => {
    if (isFeatured) return
    if (activeFilter && clubsRef.current) {
      window.scrollTo({
        top: clubsRef.current.offsetTop - 140,
        behavior: 'smooth',
      })
    }
  }, [activeFilter, isFeatured])

  useEffect(() => {
    analytics.events.viewedHomepage()
  }, [])

  const getClubs = () => {
    if (isFetching) return <Loader />
    const results = filteredClubs?.results

    if (!isFeatured && results?.length)
      return (
        <SearchFilterResults
          showAppDownloadCta={results.length > 20}
          results={results.map((club, i) => (
            <ClubCard
              key={i}
              className={css`
                .fds-ui--club-card-image {
                  height: 70vw;
                  ${mediaQueries.mobile} {
                    height: 232px;
                  }
                }
              `}
              club={club}
            />
          ))}
        />
      )

    return <HomeClubsList />
  }

  const containerStyles = css`
    ${mediaQueries.tablet} {
      padding: 0 ${marginEdgesRules.tablet};
    }
    ${mediaQueries.desktop} {
      padding: 0 ${marginEdgesRules.desktop};
    }
  `

  return (
    <div
      className={css`
        background-color: ${colors.uiWhite};
        ${mediaQueries.tablet} {
          padding-bottom: 80px;
        }
      `}
    >
      <HomeHero />
      <HomeClubsDiscovery />
      <SuspenseWithErrorBoundary>
        <div
          ref={clubsRef}
          className={css`
            padding-top: 16px;
            min-height: 800px;
          `}
        >
          {getClubs()}
        </div>
      </SuspenseWithErrorBoundary>
      <SuspenseWithErrorBoundary>
        <div className={containerStyles}>
          <HomeEvent />
        </div>
      </SuspenseWithErrorBoundary>
      <div className={containerStyles}>
        <DownloadAppCard />
      </div>
    </div>
  )
}

export default Home
